import React from 'react';
import { Users } from 'lucide-react';
import { uiBtn, uiBtnSegment } from '../../ui/buttonClasses';
import { uiSeatChip } from '../../ui/seatClasses';

type TransportHighlight = 'reset' | 'prev' | 'play' | 'next';

interface ArcGraphHeaderProps {
  currentStroke: number;
  totalStrokes: number;
  selectedSeat: number;
  availableSeats: number[];
  onSeatChange: (seat: number) => void;
  elapsedTime: string;
  totalTime: string;
  onOpenCrewSummary: () => void;
  isPlaying: boolean;
  onTogglePlay: () => void;
  onReset: () => void;
  onStepPrev: () => void;
  onStepNext: () => void;
  canStepPrev: boolean;
  canStepNext: boolean;
  transportHighlight: TransportHighlight;
  onTransportHighlight: (highlight: TransportHighlight) => void;
  playbackSpeed: number;
  speedOptions: readonly number[];
  onPlaybackSpeedChange: (speed: number) => void;
  nextFlaggedStrokeNumber: number | null;
  onJumpToNextIssue: () => void;
}

const ArcGraphHeader: React.FC<ArcGraphHeaderProps> = ({
  currentStroke,
  totalStrokes,
  selectedSeat,
  availableSeats,
  onSeatChange,
  elapsedTime,
  totalTime,
  onOpenCrewSummary,
  isPlaying,
  onTogglePlay,
  onReset,
  onStepPrev,
  onStepNext,
  canStepPrev,
  canStepNext,
  transportHighlight,
  onTransportHighlight,
  playbackSpeed,
  speedOptions,
  onPlaybackSpeedChange,
  nextFlaggedStrokeNumber,
  onJumpToNextIssue,
}) => {
  return (
    <header className="rounded-lg border border-border bg-surface shadow-sm">
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5" role="group" aria-label="Seat">
          {availableSeats.map((seat) => (
            <button
              key={seat}
              type="button"
              onClick={() => onSeatChange(seat)}
              className={uiSeatChip(seat === selectedSeat ? 'selected' : 'default')}
              aria-pressed={seat === selectedSeat}
              aria-label={`Select seat ${seat}`}
            >
              {seat}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onOpenCrewSummary}
          className={`${uiBtn('ghost', { size: 'sm' })} shrink-0 gap-1.5`}
        >
          <Users className="h-4 w-4" aria-hidden />
          Crew summary
        </button>
      </div>

      <div
        className="flex flex-col gap-2 border-t border-rowing-gray-100 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
        aria-label="Stroke playback"
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
          <div className="ui-segment-group shrink-0" role="group" aria-label="Playback transport">
            <button
              type="button"
              onClick={() => {
                onTransportHighlight('reset');
                onReset();
              }}
              className={uiBtnSegment(transportHighlight === 'reset', 'first')}
              aria-label="Reset to first stroke"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => {
                if (!canStepPrev) return;
                onTransportHighlight('prev');
                onStepPrev();
              }}
              disabled={!canStepPrev}
              className={uiBtnSegment(transportHighlight === 'prev', 'middle')}
              aria-label="Previous stroke"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => {
                onTransportHighlight('play');
                onTogglePlay();
              }}
              aria-pressed={isPlaying}
              className={uiBtnSegment(transportHighlight === 'play', 'middle')}
              aria-label={isPlaying ? 'Pause playback' : 'Play playback'}
            >
              {isPlaying ? 'Pause' : 'Play'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!canStepNext) return;
                onTransportHighlight('next');
                onStepNext();
              }}
              disabled={!canStepNext}
              className={uiBtnSegment(transportHighlight === 'next', 'last')}
              aria-label="Next stroke"
            >
              Next
            </button>
          </div>
          <p className="text-xs tabular-nums text-rowing-gray-600">
            <span className="font-medium text-rowing-gray-900">
              {currentStroke}/{totalStrokes}
            </span>
            <span className="mx-1.5 text-rowing-gray-300" aria-hidden>
              ·
            </span>
            {elapsedTime}/{totalTime}
          </p>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
          <div className="ui-segment-group shrink-0" role="group" aria-label="Playback speed">
            {speedOptions.map((speed, index) => {
              const isSelected = playbackSpeed === speed;
              const position =
                index === 0 ? 'first' : index === speedOptions.length - 1 ? 'last' : 'middle';
              return (
                <button
                  key={speed}
                  type="button"
                  onClick={() => onPlaybackSpeedChange(speed)}
                  aria-pressed={isSelected}
                  aria-label={`${speed}× speed`}
                  className={uiBtnSegment(isSelected, position)}
                >
                  {speed}×
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={onJumpToNextIssue}
            disabled={nextFlaggedStrokeNumber == null}
            className={uiBtn('secondary', { size: 'sm' })}
            aria-label="Jump to next flagged stroke"
          >
            Next issue
          </button>
        </div>
      </div>
    </header>
  );
};

export default ArcGraphHeader;
